import T from '../services/language';
import React, {useEffect, useState} from "react";
import Loading from "../components/Loading";
import {useLocation, useNavigate} from "react-router-dom";
import {getGroupByUrl, getClientConfig, getProductAndService} from "../services/app";
import Environment from "../environment";

const App: any = React.createContext({});
//@ts-ignore
const win: any = window;

export const AppProvider = ({children}) => {
    const [ready, setReady] = useState(false);
    const [loading, setLoading] = useState(false);
    const [campaign, setCampaign] = useState();
    const [campaignError, setCampaignError] = useState(false);
    const [customer, setCustomer] = useState<any>();
    const navigate = useNavigate();
    const location: any = useLocation();
    // Customer URL contract: /{groupSlug}/{shareUrlKey}/... — the first two
    // segments uniquely identify the service group. Any route deeper than the
    // landing page (booking flow, chat, 401) keeps these same two segments at
    // the front, so we can always pull groupSlug + shareUrlKey from positions
    // [0] and [1] regardless of depth. Single-segment URLs no longer resolve;
    // they fall through to the /* NotFound route in index.tsx.
    const pathSegments: string[] = (location?.pathname || '').split('/').filter(Boolean);
    // Reserved first segments that are NOT a groupSlug — these are shared
    // routes (cross-client chat/call links) that don't belong to a specific
    // service group. Keep them out of URL resolution so we don't try to look
    // up a group named "call" or "chat".
    const RESERVED_FIRST_SEGMENTS = ['call', 'chat', '404'];
    const hasGroupContext = pathSegments.length >= 2
        && !RESERVED_FIRST_SEGMENTS.includes(pathSegments[0]);
    const groupSlug = hasGroupContext ? pathSegments[0] : "";
    const shareUrlKey = hasGroupContext ? pathSegments[1] : "";
    // Legacy alias: many downstream pages still read `campaignPath` from the
    // context. It's now just the groupSlug — keep the name for the minimum
    // disruption during this refactor; rename as a follow-up.
    const campaignPath = groupSlug;
    const [themeColors, setThemeColors] = useState({main: ""});
    const [business, setBusiness] = useState({});
    // Single-tenant: clientCode is a hardcoded constant, no longer from URL.
    const clientCode = Environment.clientCode;

    useEffect(() => {
        if (!!campaign) {
            const currentUrl = location.pathname + location.search;
            localStorage.setItem('pwaStartUrl', currentUrl);
        }
    }, [location, campaign, campaignError]);

    useEffect(() => {
        const run = async () => {
            let customerData;
            // set customer local
            if (localStorage.getItem('CUSTOMER')) {
                customerData = JSON.parse(localStorage.getItem('CUSTOMER'));
                setCustomer(customerData);
            }
            // Load the service group for the current URL. Both segments are
            // required — if hasGroupContext is false (single-segment URL, or
            // a reserved route like /call/:threadId) we skip the backend
            // lookup entirely. On a shared chat/call link we keep the default
            // theme so the page still renders.
            if (hasGroupContext && !campaign) {
                let campaignLocal;
                const result = await getGroupByUrl(groupSlug, shareUrlKey);
                if (result.status === 200) {
                    setCampaignError(false);
                    campaignLocal = result?.data?.data;
                    setCampaign(campaignLocal);
                } else {
                    if (!window.location.href.includes('/direct-chat/join')) {
                        console.log("Service group not found, redirect to 404",
                            {groupSlug, shareUrlKey, url: window.location.href});
                        setCampaignError(true);
                        navigate(`/404`)
                    } else {
                        // set default theme for call and chat
                        setThemeColors({main: "#1D896D"});
                        setBusiness({});
                        setConfig({
                            themeColor: "#1D896D",
                            logoUrl: "",
                            logoWide: "",
                            logoFavicon: ""
                        });
                        return;
                    }
                }
                // }
                if (hasGroupContext && !!customerData && !customerData?.code && !!campaignLocal && !!campaignLocal?.isRequireAccessCode && (location.pathname !== `/${groupSlug}/${shareUrlKey}/` && location.pathname !== `/${groupSlug}/${shareUrlKey}`)) {
                    localStorage.setItem('PREV_URL', location.href);
                    navigate(`/${groupSlug}/${shareUrlKey}/401`);
                }
                // setup theme
                const color = campaignLocal?.business?.themeColor || "";
                setThemeColors({main: color !== "#40a68c" ? color : ""})
                setBusiness(campaignLocal?.business);
                setConfig({
                    ...(campaignLocal?.business || {}),
                    themeColor: color,
                    logoUrl: campaignLocal?.business.logoUrl,
                    logoWide: campaignLocal?.business.logoWide,
                    logoFavicon: campaignLocal?.business.logoFavicon,
                    // Option B: surface the pss_group name so the browser tab
                    // shows "BabyVax3" (or whatever the group is called)
                    // instead of the tenant business name when the URL is a
                    // direct-to-group URL.
                    pssGroupName: campaignLocal?.pssGroupName,
                })
            }


            await refreshData(['service'], true);


            setLoading(false);
        }
        run().finally(() => {
            setReady(true);
        });
    }, []);

    const getLocalUrlParams = () => {
        const url = new URL(window.location.href);
        // Booking-flow URL: /:groupSlug/:shareUrlKey/:campaignId/:customerId/product-and-service/:productServiceType/:productServiceId/:productServiceTemplateId/...
        // (8 segments before optional tail). We only pull the fields the
        // service-refresh path needs downstream.
        const result = url.pathname.match(/\/([^\/]+)\/([^\/]+)\/([^\/]+)\/([^\/]+)\/([^\/]+)\/([^\/]+)\/([^\/]+)\/([^\/]+)/);
        return result ? {
            clientCode,
            groupSlug: result[1],
            shareUrlKey: result[2],
            campaignPath: result[1], // legacy alias
            campaignId: result[3],
            customerId: result[4],
            productServiceType: result[6],
            productServiceId: result[7],
            quantity: result[8]
        } : null;
    }

    const refreshData = async (items: Array<"campaign" | "service">, forceUpdate?: boolean) => {
        if (items.includes('service')) {
            try {
                const params = getLocalUrlParams();
                // load product service detail if not exist
                if (params?.campaignId && !!params?.productServiceId && (forceUpdate || !getLocalProductService(params?.productServiceId)?.id)) {
                    const result = await getProductAndService(params?.campaignId, params?.productServiceId);
                    if (result.status === 200) {
                        setLocalProductService(params?.productServiceId, result?.data?.data || {});
                    }
                }
            } catch (e) {
            }
        }
        if (items.includes('campaign')) {
            try {
                const result = await getGroupByUrl(groupSlug, shareUrlKey);
                if (result.status === 200) {
                    setCampaignError(false);
                    setCampaign(result?.data?.data);
                } else {
                    setCampaignError(true);
                    navigate(`/404`)
                }
            } catch (e) {
            }
        }
    }

    const loadConfig = async () => {
        const clientCacheConfig = window.localStorage.getItem(`APP_THEME_${clientCode}`);
        if (!!clientCacheConfig) {
            const result = JSON.parse(clientCacheConfig);
            setConfig(result);
            getClientConfig().then(result => {
                if (result.status === 200 && result?.data?.data) {
                    const data = result?.data?.data;
                    setConfig(data)
                }
            })
        } else {
            // upadte new
            const result = await getClientConfig();
            const data = result?.data?.data || {};
            setConfig(data)
        }
    }

    useEffect(() => {
        if (customer) {
            localStorage.setItem('CUSTOMER', JSON.stringify(customer));
        }
        if (campaign && hasGroupContext) {
            localStorage.setItem(`CAMPAIGN_${groupSlug}_${shareUrlKey}`, JSON.stringify(campaign));
        }
    }, [customer, campaign]);


    const setConfig = (data) => {
        data = data || {};
        if (!!Object.keys(data || {}).includes('createdFromOrg')) {
            localStorage.setItem(`APP_THEME_${clientCode}`, JSON.stringify(data));
        } else {
            data.createdFromOrg = true;
        }
        win.document.querySelector("title").innerText = data?.pssGroupName || data?.name || T("Booking Anywhere");
        var link: any = win.document.querySelector("link[rel~='icon']");
        if (!link) {
            link = win.document.createElement('link');
            link.rel = 'icon';
            win.document.head.appendChild(link);
        }

        if (data?.createdFromOrg) {
            win.appTheme = {
                ...data,
                color: data?.themeColor || "#1D896D",
                rawLogo: data?.logoUrl,
                logoUrl: data?.logoUrl || (win.location.origin + '/logoNull.svg'),
                isDefault: false
            };
            setThemeColors({main: win.appTheme.color});
            link.href = data?.logoFavicon || data?.logoUrl || data?.logoWide || `https://img.icons8.com/ios-filled/${win.appTheme.color.replace('#', '')}/50/circled-${data?.name?.length ? data?.name[0]?.toLowerCase() : win.location.hostname.split("")[0]}.png`;
        } else {
            win.appTheme = {
                ...data,
                rawLogo: data?.logoUrl,
                color: (win.location.origin.includes('localhost') ? "#008ff8" : "#1D896D"),
                logoUrl: win.location.origin + '/icons/logoFull.svg',
                isDefault: true
            };
            setThemeColors({main: win.appTheme.color});
            link.href = data?.logoFavicon || data?.logoUrl || data?.logoWide || win.location.origin + "/logo.svg";
        }
    }

    // save booking to cache
    const getAllBooking = () => {
        const str = localStorage.getItem(`BOOKING_${customer?.customerId}`);
        return str ? JSON.parse(str) : null;
    }
    const getCurrentBooking = (index) => {
        const bookings = getAllBooking()
        if (bookings) {
            return bookings[index] ? bookings[index] : null;
        }
        return null;
    }
    const setCurrentBooking = (booking, index) => {
        let bookings = getAllBooking();
        if (!bookings) {
            bookings = [];
        }
        bookings[index] = booking;
        localStorage.setItem(`BOOKING_${customer?.customerId}`, JSON.stringify(bookings));
    }
    const removeCurrentBookings = () => {
        localStorage.removeItem(`BOOKING_${customer?.customerId}`);
    }
    // save question to cache
    const getAllAnswers = () => {
        const str = localStorage.getItem(`ANSWERS_${customer?.customerId}`);
        return str ? JSON.parse(str) : [];
    }
    const addAnswered = (answer) => {
        const items = getAllAnswers() || [];
        items.push(answer);
        localStorage.setItem(`ANSWERS_${customer?.customerId}`, JSON.stringify(items));
    }
    const getAnswered = (packageIndex) => {
        const items = getAllAnswers() || [];
        return items.find(item => item?.packageIndex?.toString() === packageIndex?.toString())
    }
    // save service
    const getLocalProductService = (id) => {
        const str = localStorage.getItem(`SERVICE_${id}`);
        return str ? JSON.parse(str) : {};
    }

    const setLocalProductService = (id, value) => {
        localStorage.setItem(`SERVICE_${id}`, JSON.stringify(value));
    }
    // save booking portal code
    const getLocalAddress = (id) => {
        const str = localStorage.getItem(`BOOKING_POSTAL_CODE_${id}`);
        return str ? JSON.parse(str) : {};
    }

    const setLocalAddress = (id, value) => {
        localStorage.setItem(`BOOKING_POSTAL_CODE_${id}`, JSON.stringify(value));
    }
    // save add-on
    const getLocalAddon = (id) => {
        const str = localStorage.getItem(`ADDON_${id}`);
        return str ? JSON.parse(str) : [];
    }

    const setLocalAddon = (id, value) => {
        localStorage.setItem(`ADDON_${id}`, JSON.stringify(value));
    }

    return (
        <App.Provider
            value={{
                setLoading,
                T: T,
                campaign,
                customer,
                setCustomer,
                setCampaign,
                campaignPath,    // legacy alias — equal to groupSlug
                groupSlug,
                shareUrlKey,
                hasGroupContext,
                campaignError,
                getCurrentBooking, setCurrentBooking, getAllBooking, removeCurrentBookings,
                addAnswered, getAnswered,
                getLocalProductService, setLocalProductService,
                getLocalAddress, setLocalAddress,
                themeColors, setThemeColors, business,
                clientCode,
                getLocalAddon, setLocalAddon,
                refreshData,
            }}>
            {ready ? children : null}
            {loading ? (<Loading/>) : null}
        </App.Provider>
    )
}

export const useApp: any = () => React.useContext(App);
